import type { GeraeteSpeicher, SpeicherTx } from './geraetespeicher';

/**
 * Fortlaufende Belegnummern je Kasse auf dem Gerät: "B-JJJJMMTT-GGGG-NNNN" mit einer
 * Gerätekennung GGGG, die der Server je Kasse registriert (N1, Kennungsregister). Mehrere Geräte
 * derselben Kasse vergeben so nie dieselbe Nummer. Seit N1 vergibt genau ein Zähler im
 * Gerätespeicher alle Nummern (online und offline, alle Fenster); localStorage ist nur noch
 * Übernahmequelle beim ersten Start und Kompatibilitätsspiegel. Die Datenbank erzwingt
 * Eindeutigkeit (Regel 8); bei einem 409 zählt die Kasse weiter. Start-/Sonderbelege haben
 * eigene Muster.
 */
export interface Speicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
}

interface Stand {
  datum: string;
  zaehler: number;
}

function schluessel(kassenId: string): string {
  return `bilanx.belegnummer.${kassenId}`;
}

function datumKompakt(d: Date): string {
  const teile = new Intl.DateTimeFormat('de-AT', {
    timeZone: 'Europe/Vienna',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const t = (typ: string): string => teile.find((x) => x.type === typ)?.value ?? '';
  return `${t('year')}${t('month')}${t('day')}`;
}

function lies(speicher: Speicher, kassenId: string): Stand | null {
  const roh = speicher.getItem(schluessel(kassenId));
  if (roh === null) {
    return null;
  }
  try {
    const s: unknown = JSON.parse(roh);
    if (
      typeof s === 'object' &&
      s !== null &&
      typeof (s as Stand).datum === 'string' &&
      typeof (s as Stand).zaehler === 'number'
    ) {
      return s as Stand;
    }
  } catch {
    // kaputter Eintrag → neu beginnen
  }
  return null;
}

const GERAET_SCHLUESSEL = 'bilanx.geraet';

/**
 * N1 (Paketplan v4.1, Punkt 9): Der Zählerstand ist unsicher – Zähler fehlt oder ist unlesbar,
 * obwohl mit der Gerätekennung schon Nummern vergeben wurden, bzw. die Uhr steht vor dem letzten
 * Stand. Online gilt weiter der Serverschutz (409 bei vergebener Nummer); offline wird nicht
 * gepuffert, bis der Server eine neue Kennung geliefert hat (neue Nummernepoche).
 */
export class ZaehlerstandUnsicher extends Error {
  constructor() {
    super(
      'Belegnummernzähler dieser Kasse unsicher – ohne Verbindung kein Verkauf möglich, bis die Kasse online eine neue Gerätekennung erhalten hat',
    );
  }
}

/** Belegnummer aus dem gemeinsamen Zähler. */
export interface Nummernvergabe {
  nummer: string;
  /**
   * Darf diese Nummer offline gepuffert werden? Nur bei sicherem Zählerstand und einer beim
   * Server registrierten Kennung (sonst könnte eine verlorene Kennung Nummern wiederholen).
   */
  offlineErlaubt: boolean;
}

/** Gerätekennung der Installation (N1, Bereich meta). */
export interface KennungStand {
  kennung: string;
  /** Kassen, für die der Server die Kennung registriert hat. */
  registriertFuer: string[];
  /** Kassen, für die mit dieser Kennung schon Nummern vergeben wurden. */
  verwendetFuer: string[];
  /** Erhöht sich mit jedem Kennungswechsel (Codex N1-6: verspätete Antworten verwerfen). */
  generation?: number;
  /** Früher verwendete Kennungen dieser Installation – nie wieder aktivieren. */
  fruehere?: string[];
}

interface ZaehlerStand extends Stand {
  /** Zählerstand unsicher (Punkt 9) – bleibt bis zur nächsten neuen Kennung. */
  unsicher?: true;
}

const KENNUNG_META = 'kennung';
const KENNUNG_MUSTER = /^[A-Z0-9]{4}$/;
/** Merker in localStorage: diese Installation nutzt schon den gemeinsamen Zähler (N1). */
const N1_MERKER = 'bilanx.n1';

function zufaelligeKennung(zufall: () => number = Math.random): string {
  const zeichen = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let neu = '';
  for (let i = 0; i < 4; i++) {
    neu += zeichen[Math.floor(zufall() * zeichen.length) % zeichen.length] ?? 'X';
  }
  return neu;
}

function istStand(s: unknown): s is ZaehlerStand {
  return (
    typeof s === 'object' &&
    s !== null &&
    typeof (s as Stand).datum === 'string' &&
    /^\d{8}$/.test((s as Stand).datum) &&
    Number.isInteger((s as Stand).zaehler) &&
    (s as Stand).zaehler >= 0 &&
    (s as Stand).zaehler <= 9999
  );
}

function istKennungStand(k: unknown): k is KennungStand {
  return (
    typeof k === 'object' &&
    k !== null &&
    typeof (k as KennungStand).kennung === 'string' &&
    KENNUNG_MUSTER.test((k as KennungStand).kennung) &&
    Array.isArray((k as KennungStand).registriertFuer) &&
    Array.isArray((k as KennungStand).verwendetFuer)
  );
}

/** Kennung der Installation lesen; beim ersten Mal die Bestandskennung aus localStorage übernehmen. */
async function kennungIn(tx: SpeicherTx, ablage: Speicher): Promise<KennungStand> {
  const k = await tx.get<unknown>('meta', KENNUNG_META);
  if (istKennungStand(k)) {
    return k;
  }
  const alt = ablage.getItem(GERAET_SCHLUESSEL);
  return {
    kennung: alt !== null && KENNUNG_MUSTER.test(alt) ? alt : zufaelligeKennung(),
    registriertFuer: [],
    verwendetFuer: [],
  };
}

/** Größerer von zwei Ständen (gleiches Datum: höherer Zähler, sonst späteres Datum). */
function hoeher(a: Stand | null, b: Stand | null): Stand | null {
  if (a === null || b === null) {
    return a ?? b;
  }
  if (a.datum !== b.datum) {
    return a.datum > b.datum ? a : b;
  }
  return a.zaehler >= b.zaehler ? a : b;
}

/**
 * N1 (Punkte 6, 9, 12): nächste Belegnummer aus dem gemeinsamen Zähler – atomar über alle
 * Fenster der Installation (eine Transaktion im Gerätespeicher), für online und offline.
 *
 * - Erster Start: übernimmt den höheren Stand aus IndexedDB und dem alten localStorage-Zähler.
 * - Danach zählt nur der Gerätespeicher; localStorage wird als reiner Kompatibilitätsspiegel
 *   mitgeschrieben (für einen noch laufenden Alttab) und vom neuen Client nie gelesen.
 * - Fehlt der Zähler, obwohl die Kennung für diese Kasse schon Nummern vergeben hat, ist er
 *   unbrauchbar, oder steht die Uhr vor dem letzten Stand: Nummer nur online (Serverschutz 409).
 * Scheitert die Transaktion, entsteht keine Nummer.
 */
export async function vergebeBelegnummer(
  speicher: GeraeteSpeicher,
  ablage: Speicher,
  kassenId: string,
  jetzt = new Date(),
): Promise<Nummernvergabe> {
  const datum = datumKompakt(jetzt);
  const ergebnis = await speicher.transaktion(async (tx) => {
    const k = await kennungIn(tx, ablage);
    const roh = await tx.get<unknown>('zaehler', kassenId);
    const bereitsN1 = ablage.getItem(N1_MERKER) !== null;
    let stand: ZaehlerStand | null = null;
    let unsicher = false;
    if (istStand(roh)) {
      stand = roh;
      unsicher = roh.unsicher === true;
    } else if (roh !== undefined) {
      // Unlesbarer Zähler: nie bei 1 neu beginnen
      unsicher = true;
    } else if (!bereitsN1) {
      // Erster Start mit gemeinsamem Zähler: Bestandsstand übernehmen
      stand = lies(ablage, kassenId);
    } else {
      // Gerätespeicher verloren, localStorage noch da: Spiegel nur als Untergrenze, Stand unsicher
      stand = lies(ablage, kassenId);
      unsicher = stand !== null;
    }
    if (stand === null && k.verwendetFuer.includes(kassenId)) {
      unsicher = true;
    }
    if (stand !== null && stand.datum > datum) {
      unsicher = true;
    }
    const zaehler = stand !== null && stand.datum === datum ? stand.zaehler + 1 : 1;
    if (zaehler > 9999) {
      throw new Error('Tageszähler der Belegnummern erschöpft');
    }
    const neu: ZaehlerStand = unsicher ? { datum, zaehler, unsicher: true } : { datum, zaehler };
    await tx.put('zaehler', kassenId, neu);
    await tx.put('meta', KENNUNG_META, {
      ...k,
      verwendetFuer: k.verwendetFuer.includes(kassenId)
        ? k.verwendetFuer
        : [...k.verwendetFuer, kassenId],
    } satisfies KennungStand);
    // Kompatibilitätsspiegel; scheitert er, scheitert die ganze Vergabe (kein Spiegel hinter dem Stand)
    const spiegel = hoeher(lies(ablage, kassenId), { datum, zaehler });
    ablage.setItem(schluessel(kassenId), JSON.stringify(spiegel));
    ablage.setItem(GERAET_SCHLUESSEL, k.kennung);
    return {
      nummer: `B-${datum}-${k.kennung}-${String(zaehler).padStart(4, '0')}`,
      offlineErlaubt: !unsicher && k.registriertFuer.includes(kassenId),
    };
  });
  ablage.setItem(N1_MERKER, '1');
  return ergebnis;
}

/** Ist der Zählerstand dieser Kasse unsicher (Punkt 9)? */
export async function zaehlerUnsicher(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<boolean> {
  return speicher.transaktion(async (tx) => {
    const roh = await tx.get<unknown>('zaehler', kassenId);
    return roh !== undefined && (!istStand(roh) || roh.unsicher === true);
  });
}

/**
 * Codex-Prüfung 3 (Fund 4): Zählerstand als unsicher markieren – z. B. wenn Belegnummern dieser
 * Kasse wiederholt schon vergeben sind. Die Synchronisation fordert dann eine neue Gerätekennung
 * (neue Nummernepoche) an; deren Nummern kollidieren nie mit bisherigen.
 */
export async function markiereZaehlerUnsicher(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<void> {
  await speicher.transaktion(async (tx) => {
    const roh = await tx.get<unknown>('zaehler', kassenId);
    const stand: ZaehlerStand = istStand(roh) ? roh : { datum: '00000000', zaehler: 0 };
    await tx.put('zaehler', kassenId, { ...stand, unsicher: true } satisfies ZaehlerStand);
  });
}

/** Aktuelle Gerätekennung (GGGG in B-JJJJMMTT-GGGG-NNNN). */
export async function aktuelleKennung(
  speicher: GeraeteSpeicher,
  ablage: Speicher,
): Promise<string> {
  return (await festeKennung(speicher, ablage)).kennung;
}

/** Schnittstelle des Servers für das Kennungsregister. */
export interface KennungsApi {
  meldeGeraetekennung(kassenId: string, kennung?: string): Promise<{ kennung: string }>;
}

/** Tablokale Reihe, falls Web Locks fehlen. */
let kennungsReihe: Promise<unknown> = Promise.resolve();

/**
 * Kennungsabgleiche laufen installationsweit nacheinander (Web Locks; sonst je Tab) – zwei
 * gleichzeitige Abgleiche könnten sonst zwei Nummernepochen gegeneinander ausspielen.
 */
function nacheinander<T>(arbeit: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (locks !== undefined) {
    return locks.request('bilanx.kennung', arbeit);
  }
  const lauf = kennungsReihe.catch(() => undefined).then(arbeit);
  kennungsReihe = lauf;
  return lauf;
}

/** Kennung lesen und – falls neu erzeugt – sofort festhalten (jeder Leser sieht dieselbe). */
async function festeKennung(speicher: GeraeteSpeicher, ablage: Speicher): Promise<KennungStand> {
  return speicher.transaktion(async (tx) => {
    const k = await kennungIn(tx, ablage);
    await tx.put('meta', KENNUNG_META, k);
    return k;
  });
}

/**
 * N1: Gerätekennung beim Server registrieren (einmal je Kasse). Liefert der Server eine andere
 * Kennung (schon vergeben bzw. in Belegnummern verwendet), gilt sie für künftige Nummern als neue
 * Nummernepoche; alte Nummern bleiben (Regel 8).
 */
export function kennungAbgleichen(
  speicher: GeraeteSpeicher,
  ablage: Speicher,
  api: KennungsApi,
  kassenId: string,
): Promise<string> {
  return nacheinander(async () => {
    const k = await festeKennung(speicher, ablage);
    if (k.registriertFuer.includes(kassenId)) {
      return k.kennung;
    }
    const antwort = await api.meldeGeraetekennung(kassenId, k.kennung);
    return uebernehmeKennung(speicher, ablage, kassenId, antwort.kennung, k, false);
  });
}

/**
 * N1: neue Kennung vom Server anfordern (unsicherer Zählerstand): neue Nummernepoche, der
 * Zähler dieser Kasse beginnt mit der neuen Kennung von vorne – ihre Nummern sind eindeutig.
 */
export function neueKennungAnfordern(
  speicher: GeraeteSpeicher,
  ablage: Speicher,
  api: KennungsApi,
  kassenId: string,
): Promise<string> {
  return nacheinander(async () => {
    const k = await festeKennung(speicher, ablage);
    const antwort = await api.meldeGeraetekennung(kassenId);
    return uebernehmeKennung(speicher, ablage, kassenId, antwort.kennung, k, true);
  });
}

/**
 * Serverantwort übernehmen – nur, wenn sich die Kennung seit dem Lesen nicht geändert hat
 * (Generation). Eine neue Epoche gibt es nur mit einer wirklich neuen Kennung; eine früher
 * verwendete Kennung wird nie wieder aktiv (ihr Zähler stünde sonst wieder am Anfang).
 */
async function uebernehmeKennung(
  speicher: GeraeteSpeicher,
  ablage: Speicher,
  kassenId: string,
  kennung: string,
  gelesen: KennungStand,
  neueEpoche: boolean,
): Promise<string> {
  if (!KENNUNG_MUSTER.test(kennung)) {
    throw new Error('Ungültige Gerätekennung vom Server');
  }
  return speicher.transaktion(async (tx) => {
    const k = await kennungIn(tx, ablage);
    if ((k.generation ?? 0) !== (gelesen.generation ?? 0) || k.kennung !== gelesen.kennung) {
      // Verspätete Antwort: inzwischen gilt eine andere Kennung – nichts ändern
      return k.kennung;
    }
    const gewechselt = k.kennung !== kennung;
    if (gewechselt && (k.fruehere ?? []).includes(kennung)) {
      throw new Error(`Gerätekennung ${kennung} war schon in Verwendung – nicht übernommen`);
    }
    if (neueEpoche && !gewechselt) {
      throw new Error('Server lieferte keine neue Gerätekennung – Zählerstand bleibt unsicher');
    }
    if (!gewechselt) {
      await tx.put('meta', KENNUNG_META, {
        ...k,
        registriertFuer: [...new Set([...k.registriertFuer, kassenId])],
      } satisfies KennungStand);
      ablage.setItem(GERAET_SCHLUESSEL, kennung);
      return kennung;
    }
    await tx.put('meta', KENNUNG_META, {
      kennung,
      registriertFuer: [kassenId],
      verwendetFuer: [],
      generation: (k.generation ?? 0) + 1,
      fruehere: [...new Set([...(k.fruehere ?? []), k.kennung])],
    } satisfies KennungStand);
    // Neue Nummernepoche: Zähler beginnt mit der neuen Kennung bei 1 (Stand 0, ohne „unsicher“).
    // Der localStorage-Spiegel bleibt beim Höchststand (nur für Alttabs).
    await tx.put('zaehler', kassenId, { datum: '00000000', zaehler: 0 } satisfies Stand);
    ablage.setItem(GERAET_SCHLUESSEL, kennung);
    return kennung;
  });
}

export function stornoBelegnummer(original: string, jetzt = new Date()): string {
  return `ST-${original}-${String(jetzt.getTime() % 100000).padStart(5, '0')}`;
}

export function sonderbelegnummer(
  art: 'NULL' | 'MONATSBELEG' | 'JAHRESBELEG',
  jetzt = new Date(),
): string {
  const datum = datumKompakt(jetzt);
  switch (art) {
    case 'NULL':
      return `N-${datum}-${String(jetzt.getTime() % 100000).padStart(5, '0')}`;
    case 'MONATSBELEG':
      return `MB-${datum.slice(0, 4)}-${datum.slice(4, 6)}`;
    case 'JAHRESBELEG':
      return `JB-${datum.slice(0, 4)}`;
  }
}

/**
 * A5-4 (Testserver 01.11.): Stornobeleg eines Originals aus Journaltreffern – Nummer
 * `ST-<Original>` bzw. `ST-<Original>-<Ziffern>` (wie der Server prüft), Belegtyp STORNO,
 * nicht vor dem Original. null = kein Storno bekannt (der Server bleibt die letzte Instanz: 409).
 */
export function bekannterStorno(
  original: { belegnummer: string; zeitpunkt: string },
  treffer: readonly { belegnummer: string; belegtyp: string; zeitpunkt: string }[],
): string | null {
  const muster = new RegExp(
    `^ST-${original.belegnummer.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(-\\d+)?$`,
  );
  const ab = Date.parse(original.zeitpunkt);
  const s = treffer.find(
    (t) => t.belegtyp === 'STORNO' && muster.test(t.belegnummer) && !(Date.parse(t.zeitpunkt) < ab),
  );
  return s?.belegnummer ?? null;
}
